import React from "react";
import {GraduationCap, Award, Users, Globe} from 'lucide-react';

function AboutMfantsipim() {
  return (
    <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-white to-[#DC143C] bg-clip-text text-[#DC2626]">Mfantsipim School

          </h1>
          <p className="text-center text-lg text-[#DC2626]">Dwen Hwe Kan</p>
          <div className="mt-4 text-lg text-[#DC2626]">Est. 1876 • Cape Coast, Ghana</div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl overflow-hidden mb-12">
          <div className="p-8 md:p-12">
            <div className="mb-6">
              <div className="inline-block px-4 py-2 bg-[#DC143C]/10 border border-[#DC143C]/30 rounded-full text-[#DC143C] text-sm font-semibold mb-4">
                150 Years of Excellence
              </div>
            </div>

            <h2 className="text-3xl font-bold mb-6 text-white">About the School</h2>
            
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>Mfantsipim School, affectionately known as "Botwe," is Ghana's oldest and most prestigious secondary school. Established in 1876 by the Methodist Church, the school has played a pivotal role in shaping Ghana's educational landscape and producing leaders across various fields.

              </p>
              <p>
                Located in the historic city of Cape Coast, Mfantsipim has maintained its reputation for academic excellence, moral integrity, and holistic development. The school's motto, "Dwen Hwe Kan" (Think and Look Ahead), encapsulates its philosophy of preparing students to become forward-thinking leaders and responsible citizens.
              </p>
              <p>Over the past 150 years, Mfantsipim has produced numerous distinguished alumni who have made significant contributions to Ghana and the world in fields including politics, medicine, law, education, business, arts, and sports.

              </p>
            </div>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <StatCard icon={<GraduationCap />} value="150+" label="Years of Excellence" />
          <StatCard icon={<Users />} value="2000+" label="Current Students" />
          <StatCard icon={<Award />} value="100+" label="National Awards" />
          <StatCard icon={<Globe />} value="Global" label="Alumni Network" />
        </div>

        {/* Values & Traditions */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-[#DC143C]">Core Values</h3>
            <ul className="space-y-4 text-gray-300">
              <ValueItem text="Academic Excellence and Intellectual Development" />
              <ValueItem text="Moral and Spiritual Formation" />
              <ValueItem text="Leadership and Service to Community" />
              <ValueItem text="Discipline and Respect for Authority" />
              <ValueItem text="Brotherhood and Unity (Botwe Spirit)" />
            </ul>
          </div>

          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-[#DC143C]">Rich Traditions</h3>
            <ul className="space-y-4 text-gray-300">
              <ValueItem text="Founders Day Celebration" />
              <ValueItem text="Inter-House Competitions" />
              <ValueItem text="Speech and Prize Giving Day" />
              <ValueItem text="Chapel Services and Religious Activities" />
              <ValueItem text="Old Boys Association Events" />
            </ul>
          </div>
        </div>

        {/* Houses Section */}
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-xl p-8 md:p-12 mb-12">
          <h3 className="text-2xl font-bold mb-8 text-center text-[#DC143C]">The Eight Houses</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <HouseCard name="Abruquah-Monney" />
            <HouseCard name="Balmer-Acquah" />
            <HouseCard name="Bartels-Sneath" />
            <HouseCard name="Brandful-Dontwi" />
            <HouseCard name="Freeman-Aggrey" />
            <HouseCard name="Lockhart-Schweitzer" />
            <HouseCard name="Pickard-Parker" />
            <HouseCard name="Sarbah-Picot" />
          </div>
        </div>

        {/* Notable Alumni */}
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-xl p-8 md:p-12">
          <h3 className="text-2xl font-bold mb-4 text-center text-[#DC143C]">Distinguished Legacy</h3>
          <p className="text-gray-300 text-center max-w-3xl mx-auto mb-8">
            Mfantsipim has produced numerous leaders including former heads of state, supreme court justices, renowned physicians, successful entrepreneurs, and accomplished professionals who continue to make Ghana proud on the global stage.
          </p>
          <div className="text-center">
            <div className="inline-block px-6 py-2 bg-[#DC143C]/10 border border-[#DC143C]/30 rounded-full text-gray-300 bg-[#B91C1C] text-lg">Continuing the tradition of excellence since 1876

            </div>
          </div>
        </div>
      </div>
    </div>);

}

function StatCard({ icon, value, label }: any) {
  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-xl p-6 text-center hover:border-[#DC143C] transition-all">
      <div className="w-12 h-12 bg-[#DC143C]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
        <div className="text-[#DC143C]">{icon}</div>
      </div>
      <div className="text-3xl font-bold text-white mb-2">{value}</div>
      <div className="text-gray-400 text-sm">{label}</div>
    </div>);

}

function ValueItem({ text }: {text: string;}) {
  return (
    <li className="flex items-start space-x-3">
      <div className="w-2 h-2 bg-[#DC143C] rounded-full mt-2 flex-shrink-0"></div>
      <span>{text}</span>
    </li>);

}

function HouseCard({ name }: {name: string;}) {
  return (
    <div className="bg-black/50 border border-[#DC143C]/20 rounded-lg p-6 text-center hover:border-[#DC143C] hover:bg-[#DC143C]/5 transition-all">
      <div className="w-16 h-16 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-white font-bold text-xl">{name[0]}</span>
      </div>
      <div className="font-semibold text-white">{name}</div>
    </div>);

}

export default AboutMfantsipim;